import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Snackbar, Alert, Divider, ListSubheader } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import api from '../../api';

// Components
import RosterCalendarSection from './RosterCalendarSection';
import TeamReportSection from './TeamReportSection';
import MyRequestsSection from './MyRequestsSection';
import ApprovalsSection from './ApprovalsSection';

const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

const RosterDashboard = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const empId = localStorage.getItem('emp_id');
  const [activeMenu, setActiveMenu] = useState('my_own');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  // Core Data States
  const [shifts, setShifts] = useState([]);
  const [team, setTeam] = useState([]);
  
  // Calendar States
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState('');
  const [targetEmpWOs, setTargetEmpWOs] = useState([]);

  // Form States
  const [assignForm, setAssignForm] = useState({ start_date: '', end_date: '', requested_shift_id: '', reason: '' });
  const [woDate, setWoDate] = useState('');
  const [woAssignDate, setWoAssignDate] = useState('');

  // Request & Approval States
  const [myWoReqs, setMyWoReqs] = useState([]);
  const [myShiftReqs, setMyShiftReqs] = useState([]);
  const [pendingWoReqs, setPendingWoReqs] = useState([]);
  const [historyWoReqs, setHistoryWoReqs] = useState([]);
  const [pendingReqs, setPendingReqs] = useState([]);
  const [historyReqs, setHistoryReqs] = useState([]);

  useEffect(() => {
    api.get('/settings/shifts')
      .then(res => setShifts(res.data.filter(s => s.is_active)))
      .catch(err => console.warn(err));
      
    api.get(`/roster/team/${empId}`)
      .then(res => setTeam(res.data.team))
      .catch(err => console.warn(err));
      
    loadApprovals();
  }, [empId]);

  useEffect(() => {
    const targetEmp = activeMenu === 'my_own' ? empId : selectedTeamMember;
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();

    if (targetEmp && (activeMenu === 'my_own' || activeMenu === 'my_team')) {
      const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month).padStart(2, '0')}-${getDaysInMonth(month, year)}`;
      
      api.get(`/roster/${targetEmp}?start_date=${startStr}&end_date=${endStr}`)
        .then(res => setCalendarData(res.data.roster))
        .catch(err => console.warn(err));
        
      api.get(`/weekoff/my-requests/${targetEmp}`)
        .then(res => setTargetEmpWOs(res.data.filter(r => r.status === 'APPROVED')))
        .catch(err => console.warn(err));
    }

    if (activeMenu === 'req_history') {
      api.get(`/weekoff/my-requests/${empId}`)
        .then(res => setMyWoReqs(res.data))
        .catch(err => console.warn(err));
      api.get(`/roster/my-requests/${empId}`)
        .then(res => setMyShiftReqs(res.data))
        .catch(err => console.warn(err));
    }
  }, [selectedDate, activeMenu, selectedTeamMember, empId]);

  const loadApprovals = async () => {
    try {
      const shiftRes = await api.get(`/roster/approvals/${empId}`);
      setPendingReqs(shiftRes.data.pending);
      setHistoryReqs(shiftRes.data.history);

      const woRes = await api.get(`/weekoff/approvals/${empId}`);
      setPendingWoReqs(woRes.data.pending);
      setHistoryWoReqs(woRes.data.history);
    } catch (err) { console.warn(err); }
  };

  const changeMonth = (offset) => setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));

  const handleAssignShift = async (e) => {
    e.preventDefault();
    try {
      await api.post('/roster/assign', {
        emp_id: selectedTeamMember,
        start_date: assignForm.start_date,
        end_date: assignForm.end_date,
        shift_id: assignForm.requested_shift_id,
      });
      showMsg('Shift assigned directly to calendar!');
      refreshCalendar();
    } catch (err) { showMsg('Failed to assign shift.', 'error'); }
  };

  const handleAssignWo = async (e) => {
    e.preventDefault();
    if (!selectedTeamMember) return showMsg('Please select a team member first', 'warning');
    try {
      await api.post('/weekoff/assign/manager', {
        emp_id: selectedTeamMember,
        date_str: woAssignDate,
        manager_id: empId,
      });
      showMsg('Week Off successfully assigned and validated!');
      refreshCalendar();
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to assign Week Off.', 'error'); }
  };

  const refreshCalendar = () => {
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
    const endStr = `${year}-${String(month).padStart(2, '0')}-${getDaysInMonth(month, year)}`;
    api.get(`/roster/${selectedTeamMember}?start_date=${startStr}&end_date=${endStr}`)
      .then(res => setCalendarData(res.data.roster))
      .catch(err => console.warn(err));
    
    api.get(`/weekoff/my-requests/${selectedTeamMember}`)
      .then(res => setTargetEmpWOs(res.data.filter(r => r.status === 'APPROVED')))
      .catch(err => console.warn(err));
  };

  const handleRequestChange = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/roster/request/${empId}`, assignForm);
      showMsg('Shift change requested successfully!');
      setAssignForm({ start_date: '', end_date: '', requested_shift_id: '', reason: '' });
      setActiveMenu('req_history');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to request shift.', 'error'); }
  };

  const handleWoRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/weekoff/request/${empId}`, { date_str: woDate });
      showMsg('Week Off requested successfully!');
      setWoDate('');
      setActiveMenu('req_history');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to request Week Off.', 'error'); }
  };

  const handleApproval = async (reqId, action, type) => {
    try {
      if (type === 'shift') {
        await api.put(`/roster/approve/${reqId}?action=${action}`);
      } else {
        await api.put(`/weekoff/approve/${reqId}?action=${action}`);
      }
      showMsg(`Request ${action}D successfully!`);
      loadApprovals();
    } catch (err) { showMsg('Failed to process request.', 'error'); }
  };

  // UPDATED: NavItem with theme-based styling (matches Dashboard)
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
          '&.Mui-selected .MuiListItemText-primary': { 
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  // UPDATED: Section Header with theme-based styling
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - Matching Dashboard style */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <SectionHeader title="ROSTER" />
          <NavItem id="my_own" label="My Shifts" icon={<CalendarMonthIcon fontSize="small" />} />
          <NavItem id="my_team" label="Team Shift" icon={<PeopleIcon fontSize="small" />} />
          <NavItem id="team_report" label="Team Report" icon={<AssessmentIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="REQUESTS" />
          <NavItem id="shift_req" label="Shift Change" icon={<SwapHorizIcon fontSize="small" />} />
          <NavItem id="wo_req" label="Week Off" icon={<BeachAccessIcon fontSize="small" />} />
          <NavItem id="req_history" label="My Requests" icon={<HistoryIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="APPROVALS" />
          <NavItem id="app_pending" label="Pending" icon={<HourglassEmptyIcon fontSize="small" />} />
          <NavItem id="app_history" label="History" icon={<FactCheckIcon fontSize="small" />} />
          
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        
        {(activeMenu === 'my_own' || activeMenu === 'my_team') && (
          <RosterCalendarSection 
            activeMenu={activeMenu}
            selectedDate={selectedDate}
            changeMonth={changeMonth}
            calendarData={calendarData}
            shifts={shifts}
            team={team}
            selectedTeamMember={selectedTeamMember}
            setSelectedTeamMember={setSelectedTeamMember}
            assignForm={assignForm}
            setAssignForm={setAssignForm}
            woAssignDate={woAssignDate}
            setWoAssignDate={setWoAssignDate}
            handleAssignShift={handleAssignShift}
            handleAssignWo={handleAssignWo}
            targetEmpWOs={targetEmpWOs}
          />
        )}

        {activeMenu === 'team_report' && (
          <TeamReportSection empId={empId} team={team} shifts={shifts} />
        )}

        {(activeMenu === 'shift_req' || activeMenu === 'wo_req' || activeMenu === 'req_history') && (
          <MyRequestsSection 
            activeMenu={activeMenu}
            shifts={shifts}
            assignForm={assignForm}
            setAssignForm={setAssignForm}
            woDate={woDate}
            setWoDate={setWoDate}
            handleRequestChange={handleRequestChange}
            handleWoRequest={handleWoRequest}
            myShiftReqs={myShiftReqs}
            myWoReqs={myWoReqs}
          />
        )}

        {(activeMenu === 'app_pending' || activeMenu === 'app_history') && (
          <ApprovalsSection 
            activeMenu={activeMenu}
            pendingReqs={pendingReqs}
            historyReqs={historyReqs}
            pendingWoReqs={pendingWoReqs}
            historyWoReqs={historyWoReqs}
            handleApproval={handleApproval}
            shifts={shifts}
            team={team}
          />
        )}
        
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default RosterDashboard;